/**
 * Fetching a canvas's formatted steps can be slow, so we grab them in batches.
 * Sending a request per batch all at once can cause 502s and 504s, so this file
 * contains the code for managing these requests in a fault tolerant way.
 *
 * This is currently used as a stopgap in ko code, but could be adapted to
 * dashboard api in the future.
 */

// eslint-disable-next-line local-eslint-rules/enforce-namespace-modularity
import type { CanvasStepDocument } from "@dashboardApi/apis/canvas/common/types";

import logger from "@lib/datadogLogger";
import { FetchRequestError } from "@lib/errors";
import type { FetchOptions } from "@lib/fetch";
import brazeFetch from "@lib/fetch";

import type { FetchStepDataProps } from "./types";
import { flatten, wait, shuffle, evenChunk } from "./utils";

/** How many steps to fetch per request. */
const batchSize = 8;

/** Minimum batch size to fetch. */
const minBatchSize = 2;

/** How long a request must have taken for us to divide it into smaller requests. */
const divideRequestDurationLimit = 15 * 1000;

/** Max retries. */
const maxAttempts = 3;

/** How long to wait before retries. */
const retryDelay = 100;

export async function fetchStepData(
  signal: AbortSignal | undefined,
  props: FetchStepDataProps
): Promise<CanvasStepDocument[]> {
  if (props.stepIds.length === 0) {
    return [];
  }

  // Deduplicate and shuffle the ids before proceeding. Ids can sometimes come
  // in type order, so all message steps wind up in the same request. Those take
  // longer to process, so can lead to timeouts.
  const deduplicatedStepIds = Array.from(new Set(props.stepIds));
  shuffle(deduplicatedStepIds);

  const fetchStart = Date.now();

  const controller = new AbortController();
  // Forward abort signal.
  if (signal) {
    signal.addEventListener("abort", () => {
      controller.abort();
    });
  }

  const requests = evenChunk(deduplicatedStepIds, batchSize).map((chunk) =>
    fetchStepDataWithRetry({ ...props, stepIds: chunk }, { signal: controller.signal })
  );

  let failed = false;
  return await Promise.all(requests)
    .then(flatten)
    .catch((e) => {
      failed = true;
      throw e;
    })
    .finally(() => {
      // If any of the requests fail, abort any other requests.
      controller.abort();

      logger.userAction("canvas-steps-loaded", {
        canvasId: props.workflowId,
        duration: Date.now() - fetchStart,
        stepCount: deduplicatedStepIds.length,
        requestCount: requests.length,
        failed,
      });
    });
}

async function fetchStepDataWithRetry(
  props: FetchStepDataProps,
  options: FetchOptions,
  attempt = 1
): Promise<CanvasStepDocument[]> {
  const start = Date.now();
  try {
    return await makeRequest(props, options);
  } catch (error) {
    if (error instanceof FetchRequestError) {
      if (error.status === 504 || error.status === 503 || error.status === 502) {
        // The request timed out or the server was busy, so try again.
        if (attempt < maxAttempts) {
          const duration = Date.now() - start;
          // Delay with some jitter.
          await wait(retryDelay + Math.random() * retryDelay * 0.1);
          // Eagerly abandon if the request has been cancelled.
          if (options.signal?.aborted) {
            throw error;
          }

          // If the request appears to have taken a long time before failing,
          // subdivide the requests so that they're smaller and hopefully can
          // complete before timeout. Because of the browser concurrency limit,
          // this increase in total requests shouldn't actually overwhelm the
          // server.
          if (duration >= divideRequestDurationLimit) {
            const { stepIds } = props;
            const chunkSize = Math.max(Math.ceil(stepIds.length / 2), minBatchSize);
            if (chunkSize > stepIds.length) {
              return Promise.all(
                evenChunk(stepIds, chunkSize).map((chunk) =>
                  fetchStepDataWithRetry({ ...props, stepIds: chunk }, options, attempt + 1)
                )
              ).then(flatten);
            }
          }

          return fetchStepDataWithRetry(props, options, attempt + 1);
        }
      }
    }
    throw error;
  }
}

function makeRequest(
  { appGroupId, workflowId, stepIds, viewActiveCanvas }: FetchStepDataProps,
  options: FetchOptions
) {
  return brazeFetch<CanvasStepDocument[]>(
    `/engagement/canvas_steps/${workflowId}/${appGroupId}`,
    "POST",
    {
      step_ids: stepIds,
      view_active_canvas: viewActiveCanvas,
    },
    options,
    {
      propagateFetchRequestError: false,
    }
  );
}
